import {
  Stepper as ChakraStepper,
  Flex,
  Stack,
  Step,
  StepIndicator,
  StepNumber,
  StepSeparator,
  StepStatus,
  StepTitle,
  defineStyle,
  defineStyleConfig,
  forwardRef,
  useToken
} from '@chakra-ui/react';

import Icon from '@/components/atomic/v1/icon/Icon';

export interface StepProps {
  title: string;
}

export interface StepperProps {
  steps: Array<StepProps>;
  currentStep: number;
  hideLastStep?: boolean;
}

const Stepper = forwardRef<StepperProps, 'div'>(({ steps, currentStep = 1, hideLastStep = false, ...rest }, ref) => {
  const [grey30, blue50, green50] = useToken('colors', ['status/disabled/bg', 'blue.50', 'green.50']);

  const lastStep = steps.length - 1;

  // This function changes the color of the separator based on the step status
  function getSeparatorColor(index: number) {
    if (index > currentStep - 1) {
      return grey30;
    }

    if (index === currentStep - 1) {
      return blue50;
    }

    return green50;
  }

  return (
    <ChakraStepper
      index={currentStep}
      gap={{ base: 2, md: '0' }}
      style={{ alignItems: 'start' }}
      width={{ base: 'min', md: 'auto' }}
      ref={ref}
      {...rest}
      // If hideLastStep is true, hide the Stepper on the last step page
      display={hideLastStep && currentStep === lastStep ? 'none' : 'flex'}
    >
      {steps.map((step, index) =>
        // If hideLastStep is true, prevents the last step from rendering in the Stepper
        hideLastStep && index === lastStep ? null : (
          <Step
            key={step.title}
            style={{
              alignItems: 'start'
            }}
          >
            <Stack align="start" direction={{ base: 'row', md: 'column' }} rowGap={4}>
              <Flex w="100%" align="start">
                <StepIndicator>
                  <StepStatus
                    complete={<Icon name="checkmark-thick" color="status/success/default" size={48} />}
                    incomplete={<StepNumber />}
                    active={<StepNumber />}
                  />
                </StepIndicator>
                <StepSeparator
                  style={{
                    margin: 0,
                    height: 4,
                    backgroundColor: getSeparatorColor(index)
                  }}
                />
              </Flex>
              <StepTitle>{step.title}</StepTitle>
            </Stack>
            <StepSeparator
              style={{
                margin: 0,
                height: 4,
                backgroundColor: getSeparatorColor(index)
              }}
            />
          </Step>
        )
      )}
    </ChakraStepper>
  );
});

export default Stepper;

const baseStyle = defineStyle({
  indicator: {
    justifyContent: 'center',
    align: 'center',
    '&[data-status=complete]': {
      background: 'status/success/bg'
    },
    '&[data-status=active]': {
      background: 'status/information/default'
    },
    '&[data-status=incomplete]': {
      background: 'status/disabled/bg',
      borderColor: 'status/disabled/bg'
    },
    width: { base: 6, md: 12 },
    height: { base: 6, md: 12 }
  },
  number: {
    '&[data-status=active]': {
      color: 'text/reverse',
      textStyle: { base: 'heading.xs', md: 'heading.m' }
    },
    '&[data-status=incomplete]': {
      color: 'status/disabled/text',
      textStyle: { base: 'heading.xs', md: 'heading.m' }
    }
  },
  separator: {
    position: 'relative',
    top: '22px',
    display: { base: 'none', md: 'inline' }
  },
  step: {
    gap: 0,
    display: 'flex',
    flexDirection: 'row'
  },
  title: {
    alignItems: 'start',
    textStyle: 'heading.xs',
    whiteSpace: { base: 'nowrap', md: 'normal' },
    textColor: 'text/heading',
    fontWeight: 700,
    paddingRight: { base: 0, md: 2 },
    '&[data-status=complete]': {
      display: { base: 'none', md: 'inline' }
    },
    '&[data-status=incomplete]': {
      display: { base: 'none', md: 'inline' }
    }
  }
});

export const stepperTheme = defineStyleConfig({
  baseStyle
});
